import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import WarningBanner from "../../helper/WarningBanner";
import { useGetCommunityDetailsQuery } from "../../slices/ExploreCustomers.slice";
import CommunityAnnotations from "./CommunityAnnotations";
import CommunityChart from "./CommunityChart";
import CommunityTable from "./CommunityTable";

const CommunityDetailsContainer = (props: any) => {
  // Properties
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("id");
  const [filteredItems, setFilteredItems] = React.useState([]);
  const [chartFilter, setChartFilter] = React.useState({
    filterKey: "",
    value: ""
  });
  const [filterChData, setFilterChData] = React.useState<any>({
    productFamily: undefined
  });
  const { data, isLoading, error } = useGetCommunityDetailsQuery(tenantId);

  // Events
  const HandleAPIExcpetion = () => (error && <DisplayAPIsException error={error} height={250} />) || "";

  const customProps = {
    tenantId: tenantId,
    tenantName: props?.tenantName
  };

  // Hooks
  useEffect(() => {
    logEvent("Community Tab", "Customer Page", "CustomerDetailsContainer", customProps);
  }, []);

  useEffect(() => {
    setFilteredItems(data?.communityDetailsData ?? []);
  }, [data]);

  useEffect(() => {
    const filteredData = (data?.communityDetailsData || []).filter(({ productFamily }: any) => {
      const filters: { [key: string]: any } = {
        ...filterChData,
        [chartFilter.filterKey]: chartFilter.value
      };

      if (!chartFilter.filterKey) {
        return true;
      }

      return filters.productFamily === undefined || filters.productFamily === productFamily;
    });
    setFilteredItems(filteredData);
    setFilterChData(() => {
      if (!chartFilter.filterKey) {
        return {
          productFamily: undefined
        };
      }
      return {
        ...filterChData,
        [chartFilter.filterKey]: chartFilter.value
      };
    });
  }, [chartFilter, data]);

  const getDistinctExceptCCP = (items: any[]) => {
    const seen = new Set();
    return items.filter((item) => {
      // Create a key from all properties except 'community'
      const { community, ...rest } = item;
      void community;
      const key = JSON.stringify(rest);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  return (
    <>
      {!isLoading && !error && filteredItems?.length > 0 && (
        <div style={{ marginLeft: 10, marginRight: 10 }}>
          <WarningBanner message="Note: All data is restricted to the last 12 months from the current month" />
        </div>
      )}
      <div style={{ display: "flex" }}>
        {(filteredItems?.length && (
          <div style={{ width: "30%" }}>
            <CommunityAnnotations annotationData={filteredItems} kustoLink={data?.communityDetailsDataKustoLink} />
          </div>
        )) ||
          (isLoading && (
            <div style={{ width: "30%" }}>
              <CustomShimmer columnCount={2} rowCount={12} styles={{ root: { height: 245 } }} />
            </div>
          ))}
        <div style={{ width: "70%", display: "flex" }}>
          <CommunityChart
            loading={isLoading}
            onSelectedChartFilter={setChartFilter}
            options={{
              filteredData: getDistinctExceptCCP(filteredItems),
              originalData: data?.communityDetailsData || [],
              chartOptions: [
                {
                  id: 1,
                  field: "productFamily",
                  key: "customer-view-customer-engagements-distribution-chart-by-productFamily",
                  title: "Engagement Distribution",
                  type: "donut",
                  kustoLink: data?.communityKustoLink
                }
              ]
            }}
          />
        </div>
      </div>
      {isLoading ? (
        <CustomShimmer columnCount={8} />
      ) : (
        (!error && (
          <div style={{ marginLeft: 3, marginRight: 3 }}>
            <CommunityTable
              tableData={getDistinctExceptCCP(filteredItems)}
              tenantName={props.tenantName}
              communityDetailsTableKustoLink={data?.communityDetailsDataKustoLink}
            />
          </div>
        )) ||
        ""
      )}
      <HandleAPIExcpetion />
    </>
  );
};

export default CommunityDetailsContainer;
